/**
 * Created by claudio iozzia on 11/07/2017.
 */
import {
  evaluateUntilFirstValidatorOutput,
  IValidatorOutputFn,
  ValidatorFullOutputFn,
  ValidatorOutputFn
} from "./IValidator";
import {Validator_Common} from "./validator_common";
import {EMPTY_ENTITY_ALERT, EntityAlert, EntityError} from "../EntityAlert";
import {tassign, Utils} from "../../common/utils";
import {IAppSpec} from "../../Redux/selector.state";
import {Ic, IEntityAlert, ITranslatedObj} from "../common";
import {Range} from "../../common/Range";
import {isNull} from "util";

export const EMPTY_NOTE_LED: INoteLed = {
  VF: null,
  IF: null,
  PF: null,
  Rd: null,
  Vout: null,
  Iout: null,
  Pout: null,
  Parallel: null,
  Serial: null
};

export interface INoteLed {
  VF: ITranslatedObj;
  IF: ITranslatedObj;
  PF: ITranslatedObj;
  Rd: ITranslatedObj;
  Vout: ITranslatedObj;
  Iout: ITranslatedObj;
  Pout: ITranslatedObj;
  Parallel: ITranslatedObj;
  Serial: ITranslatedObj;
}


const validatorFn_SingleOutput_VF_Required: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj = null;
  const _vOut = spec.spec.value.output.outputs[o].voltage;

  if (Utils.isValueEmpty(_vOut)) {
    result = {
      key: 'vf is required'
    };
  }

  return new EntityError(result);
};

const validatorFn_SingleOutput_VF_Positive: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  const _vOut = spec.spec.value.output.outputs[o].voltage;
  const result = {
    key: Utils.acceptPositive(_vOut, 'vf')
  };

  return new EntityError(result);
};


const validatorFn_SingleOutput_IF_Required: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj = null;
  const _iOut = spec.spec.value.output.outputs[o].current;

  if (Utils.isValueEmpty(_iOut)) {
    result = {
      key: 'if is required'
    };
  }

  return new EntityError(result);
};

const validatorFn_SingleOutput_IF_Positive: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj = null;
  const _iOut = spec.spec.value.output.outputs[o].current;

  result = {
    key: Utils.acceptPositive(_iOut, "if")
  };

  return new EntityError(result);
};

const validatorFn_RD_Required: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  let result: ITranslatedObj | null = null;
  const _Rd = spec.spec.value.output.ledParam.rd;

  if (Utils.isValueEmpty(_Rd)) {
    result = {
      key: 'rd is required'
    };
  }

  return new EntityError(result);
};

const validatorFn_RD_Positive: ValidatorOutputFn = (a: Ic, spec: IAppSpec, o: number): IEntityAlert => {
  const _Rd = spec.spec.value.output.ledParam.rd;
  const result = {
    key: Utils.acceptPositive(_Rd, 'rd')
  };

  return new EntityError(result);
};


export class Validator_LED_Common extends Validator_Common {

  public static rangeWithinDomain(domain: Range, range: Range): Range {
    return domain.intersect(range);
  }

  public static validate_num(n: number, range: Range, nLabel: string, domain: Range = null): ITranslatedObj {
    const _domain = isNull(domain) ? range : domain;
    const _range = Validator_LED_Common.rangeWithinDomain(_domain, range);

    return (_range.min > _domain.min)
      ? Utils.acceptInRange(n, _range.min, _range.max, nLabel, "")
      : Utils.acceptUpTo(n, _range.max, nLabel, "");
  }

  protected validation(ic: Ic, spec: IAppSpec): INoteLed {
    const iDetails: INoteLed = tassign(EMPTY_NOTE_LED);

    const entityAlert_VF = evaluateUntilFirstValidatorOutput(this.singleOutputV())(ic, spec, 0);
    const entityAlert_IF = evaluateUntilFirstValidatorOutput(this.singleOutputI())(ic, spec, 0);
    const entityAlert_Rd = evaluateUntilFirstValidatorOutput(this.ledRd())(ic, spec, 0);

    iDetails.VF = entityAlert_VF.hasError ? entityAlert_VF.translatedErrors[0] : null;
    iDetails.IF = entityAlert_IF.hasError ? entityAlert_IF.translatedErrors[0] : null;
    iDetails.Rd = entityAlert_Rd.hasError ? entityAlert_Rd.translatedErrors[0] : null;
    return iDetails;
  }

  protected ledRd(): ValidatorOutputFn[] {
    return [validatorFn_RD_Required, validatorFn_RD_Positive];
  }

  protected singleOutputV(): ValidatorOutputFn[] {
    return [validatorFn_SingleOutput_VF_Required, validatorFn_SingleOutput_VF_Positive];
  }

  protected singleOutputI(): ValidatorOutputFn[] {
    return [validatorFn_SingleOutput_IF_Required, validatorFn_SingleOutput_IF_Positive];
  }

  protected get fullOutput(): ValidatorFullOutputFn {
    return (ic: Ic, spec: IAppSpec) => {
      const note = this.validation(ic, spec);
      return {
        outputs: spec.spec.value.output.outputs.map(x => {
          return {
            voltage: new EntityError(note.VF),
            current: new EntityError(note.IF),
            power: new EntityError(note.PF)
          };
        }),
        globalOutput: {
          voltage: new EntityError(note.Vout),
          current: new EntityError(note.Iout),
          power: new EntityError(note.Pout)
        },
        ledParam: {
          parallel: new EntityError(note.Parallel),
          serial: new EntityError(note.Serial),
          rd: new EntityError(note.Rd),
          selectedLed: null
        }
      };
    };
  }
}
